import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import axios from 'axios';

import { ROOT_URL_V2, getCSRFToken } from '../../config';
import * as api from '../../epics/api';
import FreelancerDetailsHeader from './FreelancerDetailsHeader';
import FreelancerOtherDetails from './FreelancerOtherDetails';
import FreelancerDetailsAvailability from './FreelancerDetailsAvailability';
import FreelancerDetailsDocuments from './FreelancerDetailsDocuments';
import FreelancerDetailsAdminComments from './FreelancerDetailsAdminComments';
import FreelancerDetailsDVLACheckSessions from './FreelancerDetailsDVLACheckSessions';
import FreelancerDetailsDVLAConsents from './FreelancerDetailsDVLAConsents';
import FreelancerDetailsContact from './FreelancerDetailsContact';
import FreelancerDetailsHealth from './FreelancerDetailsHealth';
import FreelancerDetailsLocation from './FreelancerDetailsLocation';
import FreelancerDetailsRolesAndSkills from './FreelancerDetailsRolesAndSkills';
import FreelancerDetailsReviews from './FreelancerDetailsReviews';
import FreelancerDetailsSettings from './FreelancerDetailsSettings';
import FreelancerDetailsStrikes from './FreelancerDetailsStrikes';
import FreelancerInterviewStatements from './FreelancerDetailsContractForServices';
import FreelancerDetailsWorkHistory from './FreelancerDetailsWorkHistory';
import { freelancerRolesDetails } from '../../consts';
import history from '../../history';
import {
    TFreelancerData,
    TRoles,
    TDocuments,
    TUser,
    TServicesStructure,
    TAdminComments,
    TDVLACheckSessions,
    TDVLAConsents,
    TPageNotification,
} from '../../types';
import './styles/index.scss';

export type TFreelancerDetailsProps = {
    match: {
        path?: string;
        url?: string;
        isExact?: boolean;
        params?: {
            id: string;
        };
    };
    user: TUser;
    notification: TPageNotification;
    servicesStructure: TServicesStructure;
};

export type TDocumentRejectionReason = {
    label: string;
    value: string;
}

export type TWeeklyHoursOption = {
    label: string;
    value: number;
}

export type TGenderOption = {
    label: string;
    value: string;
}

export type TCommentCategory = {
    id: number;
    name: string;
    active: boolean;
}

type TFreelancerDetailsState = {
    freelancerData: TFreelancerData;
    freelancerAvailability: boolean | null;
    travelDistanceOptions: any[];
    documentRejectionReasonOptions: TDocumentRejectionReason[];
    weeklyHoursOptions: TWeeklyHoursOption[];
    genderOptions: TGenderOption[];
    documents: TDocuments;
    commentCategories: TCommentCategory[]
    comments: TAdminComments;
    reviews: TDocuments;
    strikes: TDocuments;
    stats: TDocuments;
    roles: TRoles;
    dvlaCheckSessions: TDVLACheckSessions;
    consents: TDVLAConsents;
};

export class FreelancerDetailsRaw extends Component<
    TFreelancerDetailsProps,
    TFreelancerDetailsState
> {
    defaultAPIStates: any = {
        meta: null,
        content: [],
        loading: false,
        hasMoreItems: false,
    };

    // TFreelancerDetailsState
    constructor(props: TFreelancerDetailsProps) {
        super(props);
        this.state = {
            freelancerData: null,
            freelancerAvailability: null,
            travelDistanceOptions: [],
            documentRejectionReasonOptions: [],
            weeklyHoursOptions: [],
            genderOptions: [],
            documents: { ...(this.defaultAPIStates as TDocuments) },
            commentCategories: [],
            comments: { ...this.defaultAPIStates },
            reviews: { ...this.defaultAPIStates },
            strikes: { ...this.defaultAPIStates },
            stats: { ...this.defaultAPIStates },
            roles: { ...(this.defaultAPIStates as TRoles) },
            dvlaCheckSessions: { ...this.defaultAPIStates },
            consents: { ...this.defaultAPIStates }
        };
    }

    componentDidMount() {
        const { id } = this.props.match.params;
        const { isAgent } = this.props.user;

        if (id) {
            this.fetchTravelDistance().then(() => {
                // eslint-disable-next-line consistent-return
                this.fetchFreelancerDataAsync(id).then((response: any) => {
                    const freelancer = response.data.content;

                    this.fetchListOf('reviews', `freelancers/${id}/reviews/`);

                    if (isAgent) {
                        this.fetchStatsAsyc(id);
                        this.fetchFreelancerAvailabilityAsync(id);
                        this.fetchCommentCategories();
                        this.fetchListOf('comments', `freelancers/${id}/comments/`);
                        this.fetchListOf('reviews', `freelancers/${id}/reviews/`);
                        this.fetchListOf('documents', `documents/?freelancer=${id}`);
                        this.fetchDocumentRejectionReasonOptions();
                        this.fetchWeeklyHoursOptions();
                        this.fetchGenderOptions();
                        this.fetchListOf('strikes', `freelancers/${id}/strikes/`);
                        this.fetchListOf('dvlaCheckSessions', `freelancers/${id}/dvla-check-sessions/`);
                        this.fetchListOf('consents', `freelancers/${id}/dvla-consents/`);

                        const serviceFilterName = freelancerRolesDetails[freelancer.service_key];

                        return this.fetchListOf('roles', `freelancers/${id}/${serviceFilterName}/`);
                    }
                });
            });
        }
    }

    setLoading = (
        stateName: keyof TFreelancerDetailsState,
        isLoading: boolean,
    ) => {
        if (
            stateName !== 'freelancerAvailability' &&
            stateName !== 'travelDistanceOptions' &&
            stateName !== 'freelancerData'
        ) {
            // eslint-disable-next-line arrow-body-style
            this.setState(state => ({
                ...state,
                [stateName]: {
                    ...state[stateName],
                    loading: isLoading,
                },
            }));
        }
    };

    setFreelancerData = (data: TFreelancerData) => {
        this.setState({
            freelancerData: data,
        });
    };

    fetchListOf = (
        stateName: keyof TFreelancerDetailsState,
        pathname: string,
    ) => {
        this.setLoading(stateName, true);

        return api
            .executeHttpRequest(pathname, 'get')
            .then(response => {
                if (
                    stateName !== 'freelancerAvailability' &&
                    stateName !== 'travelDistanceOptions' &&
                    stateName !== 'freelancerData'
                ) {
                    this.setState(state => ({
                        ...state,
                        [stateName]: {
                            ...state[stateName],
                            meta: response.data.meta,
                            content: response.data.content,
                            hasMoreItems: !!response.data.meta?.next,
                        },
                    }));
                }

                this.setLoading(stateName, false);
            })
            .catch(() => {
                this.setLoading(stateName, false);
            });
    };

    getMoreData = (stateName: keyof TFreelancerDetailsState) => {
        api.executeHttpRequest(
            // @ts-ignore
            this.state[stateName].meta.next,
            'get',
            {},
            false,
        ).then(response => {
            if (
                stateName !== 'freelancerAvailability' &&
                stateName !== 'travelDistanceOptions' &&
                stateName !== 'freelancerData'
            ) {
                // eslint-disable-next-line arrow-body-style
                this.setState(state => ({
                    ...state,
                    [stateName]: {
                        ...state[stateName],
                        meta: response.data.meta,
                        content: [
                            // @ts-ignore
                            ...state[stateName].content,
                            ...response.data.content,
                        ],
                        hasMoreItems: !!response.data.meta.next,
                    },
                }));
            }
        });
    };

    fetchTravelDistance = () =>
        axios({
            method: 'GET',
            url: `${ROOT_URL_V2}travel_distances/`,
        })
            .then(response => {
                this.setState({
                    travelDistanceOptions: response.data.content,
                });
            })
            .catch(error => {
                const status = error.response.status.toString();
                if (status.substring(0, 2) === '40') {
                    history.push('/');
                }
            });

    fetchFreelancerDataAsync = (id: string) =>
        axios({
            method: 'GET',
            url: `${ROOT_URL_V2}freelancers/${id}/`,
        })
            .then(response => {
                this.setFreelancerData(response.data.content);
                return response;
            })
            .catch(error => {
                const status = error?.response.status.toString();
                if (status.substring(0, 2) === '40') {
                    history.push('/');
                }
            });

    fetchFreelancerAvailabilityAsync = (id: string) =>
        axios({
            method: 'GET',
            url: `${ROOT_URL_V2}freelancers/${id}/availability/`,
        })
            .then(response => {
                this.setState({
                    freelancerAvailability: response.data.content,
                });
            })
            .catch(error => {
                throw error;
            });

    updateFreelancerData = (fields: any) => {
        const { id } = this.props.match.params;

        return axios({
            method: 'PATCH',
            url: `${ROOT_URL_V2}freelancers/${id}/`,
            data: fields,
            headers: {
                'X-CSRFToken': getCSRFToken(),
            },
        })
            .then(response => {
                this.setFreelancerData(response.data.content);
                return response;
            })
            .catch(error => {
                throw error;
            });
    };

    fetchStatsAsyc = (id: string) => {
        this.setLoading('stats', true);

        api.executeHttpRequest(`freelancers/${id}/stats/`, 'get')
            .then(response => {
                this.setState({
                    stats: {
                        content: response.data.content,
                        loading: false,
                    },
                });
                this.setLoading('stats', false);
            })
            .catch(error => {
                console.error(error);
            });
    };

    fetchDocumentRejectionReasonOptions = () =>
        axios({
            method: 'GET',
            url: `${ROOT_URL_V2}documents/agent-rejection-reasons/`,
        })
            .then(response => {
                this.setState({
                    documentRejectionReasonOptions: response.data.content,
                });
            })
            .catch(error => {
                console.error(error);
            });

    fetchWeeklyHoursOptions = () =>
        axios({
            method: 'GET',
            url: `${ROOT_URL_V2}freelancers/weekly-hours-choices/`,
        })
            .then(response => {
                this.setState({
                    weeklyHoursOptions: response.data.content,
                });
            })
            .catch(error => {
                console.error(error);
            });

    fetchGenderOptions = () =>
        axios({
            method: 'GET',
            url: `${ROOT_URL_V2}freelancers/gender-choices/`,
        })
            .then(response => {
                this.setState({
                    genderOptions: response.data.content,
                });
            })
            .catch(error => {
                console.error(error);
            });

    fetchCommentCategories = () =>
        api.executeHttpRequest('comment-categories/', 'get')
            .then(response => this.setState({
                commentCategories: response.data.content
            })).catch(error => {
                console.error(error);
            });

    updateFreelancerPhoto = (file: any) => {
        const { id } = this.props.match.params;
        const data = new FormData();
        data.append('photo', file);

        axios({
            method: 'PATCH',
            url: `${ROOT_URL_V2}freelancers/${id}/`,
            data,
            headers: {
                'content-type': 'multipart/form-data',
                'X-CSRFToken': getCSRFToken(),
            },
        })
            .then(response => {
                this.setFreelancerData(response.data.content);
            })
            .catch(error => {
                throw error;
            });
    };

    render() {
        const {
            freelancerData,
            freelancerAvailability,
            travelDistanceOptions,
            documentRejectionReasonOptions,
            weeklyHoursOptions,
            genderOptions,
            reviews,
            strikes,
            stats,
            roles,
            documents,
            commentCategories,
            comments,
            dvlaCheckSessions,
            consents,
        } = this.state;
        const editionEnabled = this.props.user.permissions.includes(
            'freelancer.change_freelancer',
        );

        const { isClient, isAgent, isSuperUser } = this.props.user;

        if (!freelancerData) {
            return (
                <div className="fetching-freelancer-info">
                    <i className="icon icon-loading-1" />
                </div>
            );
        }

        return (
            <div
                className="freelancer-details-container"
                data-testid="detailsContainer"
                id="onlyTestId"
            >
                <FreelancerDetailsHeader
                    isAgent={isAgent}
                    isClient={isClient}
                    freelancerData={freelancerData}
                    setFreelancerData={this.setFreelancerData}
                    stats={stats}
                    onPhotoUpload={this.updateFreelancerPhoto}
                    onSave={this.updateFreelancerData}
                    notification={this.props.notification}
                />
                {(isAgent && freelancerData.interview_statements) && (
                    <FreelancerInterviewStatements
                        interviewer={freelancerData.interview_statements.interviewer}
                        interviewDate={freelancerData.interview_statements.interview_date}
                        hasSignedContract={freelancerData.interview_statements.has_signed_contract}
                        identityConfirmed={freelancerData.interview_statements.identity_confirmed}
                    />
                )}
                <FreelancerDetailsReviews
                    isAgent={isAgent}
                    reviews={reviews}
                    getMoreData={this.getMoreData}
                />
                <FreelancerDetailsContact
                    isAgent={isAgent}
                    isSuperUser={isSuperUser}
                    editionEnabled={editionEnabled}
                    freelancerData={freelancerData}
                    weeklyHoursOptions={weeklyHoursOptions}
                    genderOptions={genderOptions}
                    onSave={this.updateFreelancerData}
                />
                {isAgent && (
                    <>
                        <FreelancerDetailsSettings
                            freelancerData={freelancerData}
                            notification={this.props.notification}
                        />
                        <FreelancerDetailsHealth
                            editionEnabled={editionEnabled}
                            freelancerData={freelancerData}
                            onSave={this.updateFreelancerData}
                        />
                        <FreelancerOtherDetails
                            isAgent={isAgent}
                            stats={stats}
                            freelancerData={freelancerData}
                            editionEnabled={editionEnabled}
                            onSave={this.updateFreelancerData}
                        />
                    </>
                )}
                <FreelancerDetailsLocation
                    isAgent={isAgent}
                    freelancerData={freelancerData}
                    editionEnabled={editionEnabled}
                    travelDistanceOptions={travelDistanceOptions}
                    onSave={this.updateFreelancerData}
                />
                {!!isAgent && (
                    <div>
                        <FreelancerDetailsAvailability
                            freelancerData={freelancerData}
                            freelancerAvailability={freelancerAvailability}
                            editionEnabled={editionEnabled}
                            fetchFreelancerAvailabilityAsync={
                                this.fetchFreelancerAvailabilityAsync
                            }
                        />
                        <FreelancerDetailsStrikes
                            editionEnabled={editionEnabled}
                            strikes={strikes}
                            freelancerData={freelancerData}
                            getMoreData={this.getMoreData}
                            fetchListOf={this.fetchListOf}
                        />
                        <FreelancerDetailsDocuments
                            editionEnabled={editionEnabled}
                            freelancerData={freelancerData}
                            documentRejectionReasonOptions={documentRejectionReasonOptions}
                            documents={documents}
                            fetchListOf={this.fetchListOf}
                            fetchFreelancerDataAsync={this.fetchFreelancerDataAsync}
                            notification={this.props.notification}
                        />
                        <FreelancerDetailsAdminComments
                            comments={comments}
                            commentCategories={commentCategories}
                            freelancerId={freelancerData.id}
                            editionEnabled={editionEnabled}
                            fetchListOf={this.fetchListOf}
                        />
                        <FreelancerDetailsDVLACheckSessions
                            freelancerId={freelancerData.id}
                            fetchListOf={this.fetchListOf}
                            dvlaCheckSessions={dvlaCheckSessions}
                        />
                        <FreelancerDetailsDVLAConsents
                            consents={consents}
                            editionEnabled={editionEnabled}
                            freelancerData={freelancerData}
                            fetchListOf={this.fetchListOf}
                        />
                    </div>
                )}
                {isAgent && (
                    <FreelancerDetailsRolesAndSkills
                        editionEnabled={editionEnabled}
                        freelancerData={freelancerData}
                        fetchListOf={this.fetchListOf}
                        roles={roles}
                        servicesStructure={this.props.servicesStructure}
                    />
                )}
                {isAgent && (
                    <FreelancerDetailsWorkHistory
                        freelancerId={freelancerData.id}
                    />
                )}
            </div>
        );
    }
}

type TState = {
    user: TUser;
    servicesStructure: TServicesStructure;
};

function mapStateToProps(state: TState) {
    return {
        user: state.user,
        servicesStructure: state.servicesStructure,
    };
}
// @ts-ignore
export default withRouter(connect(mapStateToProps)(FreelancerDetailsRaw));
